import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import usePrevious from 'common/usePrevious';
import _get from 'lodash/get';
import _every from 'lodash/every';
import I18n from 'i18n-js';
import cx from 'classnames';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import {
  bulkUploadDeliveriesSelector,
  bulkUploadGroupingSelector,
} from 'selectors/new-bulk-upload-selectors';
import { enableMSDSelector } from 'selectors/app-selector';
import Scroller from 'components/v2/scroller';
import BulkBookingEditor from 'components/v2/anytime-booking/bulk-booking/bulk-booking-editor';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import BulkUploadPageContext from 'views/v2/new-bulk-upload/common/context';
import {
  getBURGroupingNotShowModal,
  setBURGroupingNotShowModal,
} from 'helper/storage';
import { deliveryPriceReady } from 'common/anytime-booking/delivery';

import styles from './set-up-deliveries.less';
import BulkBookingFooter from './footer';
import RouteOptimizationModalDisplay from './route-optimization-modal-display';
import PickUpKeys from './pickup-keys';
import {
  useProceedToPayment,
  useGroupSwitcher,
  usePickupDeliveries,
  useEligibleDeliveries,
  useOptmizeAmount,
} from './hooks';
import RefreshFailedQuotesButton from '../common/RefreshFailedQuotesButton';

const SetUpDeliveries = ({
  dispatch,
  deliveries,
  bulkUploadGrouping,
  enableMSD,
}) => {
  const [selectedDeliveryIDs, setSelectedDeliveries] = useState([]);
  const { stepForwardFunc } = useContext(BulkUploadPageContext);
  const [
    dontShowGroupingOptimizeModal,
    setDontShowGroupingOptimizeModal,
  ] = useState(false);
  const [
    isShowRouteOptimizationModal,
    setIsShowRouteOptimizationModal,
  ] = useState(false);
  const prevIsShowRouteOptimizationModal = usePrevious(
    isShowRouteOptimizationModal,
  );
  const bulkUploadGroupingIsGrouped = _get(bulkUploadGrouping, 'isGrouped');

  const initDeliveries = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.createNewDeliveries());
    },
    [dispatch],
  );
  useEffect(
    () => {
      setDontShowGroupingOptimizeModal(getBURGroupingNotShowModal());
      if (deliveries.length === 0) {
        setTimeout(() => {
          initDeliveries();
        }, 100);
      }
    },
    [initDeliveries],
  );
  const [currentPickUpKey, setCurrentPickUpKey] = useState(undefined);
  const onSelectPickUpKey = useCallback(
    pickUpKey => () => {
      setCurrentPickUpKey(pickUpKey);
    },
    [setCurrentPickUpKey],
  );

  const { pickUps, filteredDeliveries } = usePickupDeliveries(
    deliveries,
    currentPickUpKey,
  );

  const { totalAmount, optimizedTotalAmount } = useOptmizeAmount();

  const {
    eligibleGroupingDeliveryIDs,
    isEligibleForGrouping,
  } = useEligibleDeliveries(deliveries, pickUps);

  const { callbackPaymentClick, onProceedToPayment } = useProceedToPayment(
    setIsShowRouteOptimizationModal,
    stepForwardFunc,
    totalAmount,
    optimizedTotalAmount,
    dontShowGroupingOptimizeModal,
    isEligibleForGrouping,
    eligibleGroupingDeliveryIDs,
    enableMSD,
  );

  const setCallbackGroupSwitcher = useGroupSwitcher();

  useEffect(
    () => {
      if (pickUps && currentPickUpKey === undefined && pickUps.length > 0) {
        setCurrentPickUpKey(pickUps[0].pickUpKey);
      }
    },
    [pickUps, currentPickUpKey, setCurrentPickUpKey],
  );

  const scribeSetupDeliveriesCommonData = useMemo(
    () => {
      // for analytics tracking
      if (pickUps.length > 0) {
        const totalSenders = _get(pickUps, 'length', 0);
        const deliveriesBySender = pickUps.map(pickUp =>
          _get(pickUp, ['dropOffs', 'length']),
        );
        const totalDeliveries = deliveriesBySender.reduce(
          (partial, s) => partial + s,
          0,
        );
        return {
          totalSenders,
          deliveriesBySender,
          totalDeliveries,
        };
      }
      return undefined;
    },
    [pickUps],
  );

  useEffect(
    () => {
      // Run once
      if (scribeSetupDeliveriesCommonData) {
        dispatch(
          scribeNewBulkUploadDuck.creators.scribeExpressBukUploadSetupDeliveryDefault(
            scribeSetupDeliveriesCommonData,
          ),
        );
      }
    },
    [dispatch],
  );

  const onChangeAutoMultiStop = useCallback(
    () => {
      // Only proceed if enableMSD is true
      if (!enableMSD) {
        return;
      }

      setDontShowGroupingOptimizeModal(true);
      setCallbackGroupSwitcher(true);

      if (!bulkUploadGroupingIsGrouped) {
        dispatch(
          NewBulkUploadDuck.creators.groupingTriggerClusteringDeliveries(
            eligibleGroupingDeliveryIDs,
          ),
        );
        return;
      }
      dispatch(
        NewBulkUploadDuck.creators.groupingSwapDeliveries(
          !bulkUploadGroupingIsGrouped,
        ),
      );
    },
    [
      enableMSD,
      eligibleGroupingDeliveryIDs,
      bulkUploadGroupingIsGrouped,
      deliveries,
      dispatch,
    ],
  );

  useEffect(
    () => {
      if (isShowRouteOptimizationModal && !prevIsShowRouteOptimizationModal) {
        dispatch(
          scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryReviewMultiStop(
            undefined,
          ),
        );
      }
    },
    [isShowRouteOptimizationModal, prevIsShowRouteOptimizationModal],
  );

  const onOkRouteOptimizationModal = useCallback(
    doNotShowAgain => () => {
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryReviewMultiStop(
          'accept',
          doNotShowAgain,
        ),
      );
      if (doNotShowAgain) {
        setBURGroupingNotShowModal(true);
      }
      setIsShowRouteOptimizationModal(false);
      setDontShowGroupingOptimizeModal(true);
      dispatch(NewBulkUploadDuck.creators.groupingSwapDeliveries(true));
    },
    [dispatch, setBURGroupingNotShowModal],
  );

  const onCancelRouteOptimizationModal = useCallback(
    doNotShowAgain => () => {
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryReviewMultiStop(
          'skip',
          doNotShowAgain,
        ),
      );
      if (doNotShowAgain) {
        setBURGroupingNotShowModal(true);
      }
      setDontShowGroupingOptimizeModal(true);
      setIsShowRouteOptimizationModal(false);
      stepForwardFunc();
    },
    [dispatch, setBURGroupingNotShowModal],
  );

  const refreshFailedQuotesButton = useMemo(
    () => {
      const isAllQuotesReady = _every(deliveries, deliveryPriceReady);
      if (isAllQuotesReady) {
        return null;
      }

      return (
        <div className={cx(styles.section)}>
          <div className={cx(styles.sectionTitle)}>
            <RefreshFailedQuotesButton deliveries={deliveries} />
          </div>
        </div>
      );
    },
    [deliveries],
  );

  return (
    <Fragment>
      <div className={styles.setUpDeliveries}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_heading_senders_info_happypath',
            )}
          </h2>
          <Scroller
            className={styles.pickUpAddressesScroller}
            contentClassName={cx(styles.pickUpAddresses, {
              [styles.single]: !pickUps || pickUps.length <= 1,
            })}
          >
            <PickUpKeys
              pickUps={pickUps}
              currentPickUpKey={currentPickUpKey}
              onSelectPickUpKey={onSelectPickUpKey}
              deliveries={deliveries}
            />
          </Scroller>
        </div>
        <div className={cx(styles.section, styles.fullWidth)}>
          <h2 className={styles.sectionTitle}>
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_heading_amt_of_deliveries_happypath',
              { amountOfDeliveries: filteredDeliveries.length },
            )}
          </h2>
          {refreshFailedQuotesButton}
          <div className={styles.sectionBody}>
            <BulkBookingEditor
              key={currentPickUpKey}
              eligibleGroupingDeliveryIDs={eligibleGroupingDeliveryIDs}
              deliveries={filteredDeliveries}
              shouldShowGroupingOverlay={!callbackPaymentClick}
              selectedDeliveryIDs={selectedDeliveryIDs}
              onSelectDeliveryIDs={setSelectedDeliveries}
            />
          </div>
        </div>
      </div>
      <BulkBookingFooter
        deliveries={deliveries}
        selectedDeliveryIDs={selectedDeliveryIDs}
        scribeSetupDeliveriesCommonData={scribeSetupDeliveriesCommonData}
        onChangeAutoMultiStop={onChangeAutoMultiStop}
        disabledAutoGroup={
          !bulkUploadGroupingIsGrouped && !isEligibleForGrouping
        }
        stepForwardFunc={onProceedToPayment}
      />
      <RouteOptimizationModalDisplay
        deliveries={deliveries}
        totalAmount={totalAmount}
        optimizedTotalAmount={optimizedTotalAmount}
        isShowRouteOptimizationModal={isShowRouteOptimizationModal}
        dontShowGroupingOptimizeModal={dontShowGroupingOptimizeModal}
        onOkRouteOptimizationModal={onOkRouteOptimizationModal}
        onCancelRouteOptimizationModal={onCancelRouteOptimizationModal}
      />
    </Fragment>
  );
};

SetUpDeliveries.defaultProps = {};

SetUpDeliveries.propTypes = {
  dispatch: PropTypes.func.isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  bulkUploadGrouping: PropTypes.bool.isRequired,
  enableMSD: PropTypes.bool.isRequired,
};

export default connect(state => ({
  deliveries: bulkUploadDeliveriesSelector(state),
  bulkUploadGrouping: bulkUploadGroupingSelector(state),
  enableMSD: enableMSDSelector(state),
}))(SetUpDeliveries);
